<template>
  <!-- 常驻显示区域 -->
  <div @click="openDialog" class="custom-chip-input-container col">
    <q-input borderless dense readonly :model-value="displayText" class="custom-input underline-input"
      placeholder="点击填写" ref="inputRef">
      <template v-slot:prepend>
        <div class="chips-container">
          <transition-group name="chip-fade">
            <q-chip v-for="(item, index) in displayList" :key="`chip-${item.id || index}`" removable
              @remove="removeItem(index)" color="primary" text-color="white" size="sm" class="custom-chip">
              <div class="chip-content">
                <span class="chip-label">{{ item.label }}：</span>
                <span class="chip-value">{{ item[fieldName] }}</span>
              </div>
            </q-chip>
          </transition-group>
        </div>
      </template>

      <template v-slot:append>
        <q-icon :name="displayList.length > 0 ? 'edit' : 'add'" class="input-icon" />
      </template>
    </q-input>
  </div>

  <!-- 弹窗输入 -->
  <q-dialog v-model="show" persistent @hide="onDialogHide" ref="dialogRef">
    <q-card class="custom-dialog-card">
      <q-card-section class="dialog-header">
        <div class="text-h6 dialog-title">
          <q-icon name="settings" class="title-icon" />
          数据输入
        </div>
        <q-separator class="header-separator" />
      </q-card-section>

      <q-card-section class="dialog-content">
        <div v-for="(opt, index) in safeOptions" :key="`input-${opt.id}`" class="input-item">
          <div class="input-label">{{ opt.label }}</div>
          <q-input v-model="localMap[opt.id]" clearable borderless dense :placeholder="'请输入 ' + opt.label"
            @update:model-value="handleInputChange(opt.id, $event)" class="dialog-input underline-input"
            ref="inputFields" />
        </div>
      </q-card-section>

      <q-card-actions align="right" class="dialog-actions">
        <q-btn flat label="关闭" color="primary" @click="closeDialog" class="action-btn" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { ref, reactive, computed, watch, onBeforeUnmount, nextTick } from "vue";

const props = defineProps({
  options: { type: Array, default: () => [] }, // 完整选项数据 [{id,label}]
  data: { type: Array, default: () => [] },    // 已选择的数据 [{id, quantity}]
  fieldName: { type: String, default: "quantity" }
});

const emit = defineEmits(["update:data"]);

const show = ref(false);
const dialogRef = ref(null);
const inputRef = ref(null);
const inputFields = ref([]);

// 确保组件已挂载
const isMounted = ref(false);

// 用一个 map 保存 { id: quantity }
const localMap = reactive({});

// 组件挂载后设置标志
nextTick(() => {
  isMounted.value = true;
});

// 安全的选项列表，确保总是返回数组
const safeOptions = computed(() => {
  return Array.isArray(props.options) ? props.options : [];
});

// 安全的数据列表，确保总是返回数组
const safeData = computed(() => {
  return Array.isArray(props.data) ? props.data : [];
});

// 初始化 & 监听 data 更新
watch(
  () => safeData.value,
  (newVal) => {
    if (!isMounted.value) return;

    // 清空当前 map
    Object.keys(localMap).forEach(k => delete localMap[k]);

    // 填充新数据
    if (newVal && newVal.length > 0) {
      newVal.forEach(item => {
        if (item && item.id !== undefined) {
          localMap[item.id] = item[props.fieldName];
        }
      });
    }

    // 确保所有选项都有默认值
    safeOptions.value.forEach(opt => {
      if (opt && opt.id !== undefined && localMap[opt.id] === undefined) {
        localMap[opt.id] = "";
      }
    });
  },
  { deep: true, immediate: true }
);

// 打开弹窗
function openDialog() {
  if (!isMounted.value) return;
  show.value = true;
}

// 输入变化
function handleInputChange(id, value) {
  if (!isMounted.value) return;
  localMap[id] = value;
  emitUpdate();
}

// 删除 chip 项目
function removeItem(index) {
  if (!isMounted.value) return;
  const item = displayList.value[index];
  if (item && item.id !== undefined) {
    localMap[item.id] = "";
    emitUpdate();
  }
}

// 对话框隐藏时同步
function onDialogHide() {
  emitUpdate();
}

// 关闭弹窗
function closeDialog() {
  show.value = false;
}

// 发送更新到父组件
function emitUpdate() {
  if (!isMounted.value) return;

  const arr = Object.entries(localMap)
    .filter(([id, value]) => value && value !== "")
    .map(([id, value]) => ({
      id: Number(id),
      [props.fieldName]: value // 不再转换为数字，保留原始文本
    }));
  emit("update:data", arr);
}

// 显示 chip 的数据（带 label）
const displayList = computed(() => {
  if (!isMounted.value) return [];

  return safeOptions.value
    .map(opt => {
      if (!opt || opt.id === undefined) return null;
      return {
        id: opt.id,
        label: opt.label || "未知",
        [props.fieldName]: localMap[opt.id] || ""
      };
    })
    .filter(item => item !== null && item[props.fieldName] && item[props.fieldName] !== "");
});

// 占位显示
const displayText = computed(() =>
  displayList.value.length > 0 ? " " : ""
);

// 组件销毁前的清理
onBeforeUnmount(() => {
  isMounted.value = false;
  show.value = false;
});
</script>

<style scoped>
.custom-chip-input-container {
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 6px;
  padding: 4px 0;
}

.custom-input :deep(.q-field__control) {
  min-height: 40px;
  border-radius: 0;
  padding: 0;
  background: transparent !important;
}

.custom-input :deep(.q-field__control:before) {
  border-bottom: 2px solid #e0e0e0;
  transition: border-bottom-color 0.3s ease;
}

.custom-input :deep(.q-field__control:hover:before) {
  border-bottom: 2px solid #bdbdbd;
}

.custom-input :deep(.q-field__control:after) {
  border-bottom: 2px solid #1976d2;
  transform: scaleX(0);
  transition: transform 0.36s cubic-bezier(0.4, 0, 0.2, 1);
}

.custom-input :deep(.q-field--focused .q-field__control:after) {
  transform: scaleX(1);
}

.chips-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 2px 0;
  min-height: 30px;
  align-items: center;
}

.custom-chip {
  border-radius: 4px;
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  animation: chip-appear 0.3s ease;
  height: 24px;
}

.custom-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
}

.chip-content {
  display: flex;
  align-items: center;
  font-size: 12px;
}

.chip-label {
  font-weight: 600;
  opacity: 0.9;
}

.chip-value {
  font-weight: 400;
}

.input-icon {
  color: #757575;
  transition: all 0.3s ease;
  font-size: 18px;
}

/* 弹窗样式 */
.custom-dialog-card {
  border-radius: 10px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  min-width: 450px;
  overflow: hidden;
}

.dialog-header {
  padding-bottom: 8px;
  background: linear-gradient(135deg, #1976d2 0%, #0d47a1 100%);
  color: white;
}

.dialog-title {
  display: flex;
  align-items: center;
  color: white;
  margin-bottom: 8px;
  font-size: 1.1rem;
}

.title-icon {
  margin-right: 10px;
  font-size: 22px;
}

.header-separator {
  background-color: rgba(255, 255, 255, 0.3);
}

.dialog-content {
  padding: 20px;
}

.input-item {
  margin-bottom: 16px;
}

.input-label {
  font-weight: 500;
  margin-bottom: 5px;
  color: #424242;
  font-size: 0.85rem;
}

.dialog-input :deep(.q-field__control) {
  border-radius: 0;
  transition: all 0.3s ease;
  min-height: 40px;
  background: transparent !important;
  padding: 0;
}

.dialog-input :deep(.q-field__control:before) {
  border-bottom: 2px solid #e0e0e0;
  transition: border-bottom-color 0.3s ease;
}

.dialog-input :deep(.q-field__control:hover:before) {
  border-bottom: 2px solid #bdbdbd;
}

.dialog-input :deep(.q-field__control:after) {
  border-bottom: 2px solid #1976d2;
  transform: scaleX(0);
  transition: transform 0.36s cubic-bezier(0.4, 0, 0.2, 1);
}

.dialog-input :deep(.q-field--focused .q-field__control:after) {
  transform: scaleX(1);
}

.dialog-actions {
  padding: 12px 20px;
  border-top: 1px solid #e0e0e0;
}

.action-btn {
  border-radius: 5px;
  padding: 5px 14px;
  font-weight: 500;
  text-transform: none;
}

/* 动画效果 */
.chip-fade-enter-active,
.chip-fade-leave-active {
  transition: all 0.3s ease;
}

.chip-fade-enter-from,
.chip-fade-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

@keyframes chip-appear {
  0% {
    opacity: 0;
    transform: scale(0.8) translateY(3px);
  }

  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}
</style>